// ============================================================
// 📄 src/services/api.ts - ИСПРАВЛЕННАЯ ВЕРСИЯ
// ============================================================
// 🔧 ИСПРАВЛЕНО:
// 1. Убран лишний заголовок X-Telegram-Init-Data из authTelegram
// 2. Улучшена обработка ошибок с детальным логированием
// 3. Добавлены защиты от дублирования запросов
// 4. Корректная подстановка Bearer-токена (восстановление из localStorage)
// 5. Доп. диагностика 401 и маскирование токена в логах
// 6. ✅ НОВОЕ: Runtime валидация с zod для защиты от невалидных данных
// ============================================================

import { logger as appLogger } from '../utils/logger';
import {
  TelegramAuthResponseSchema,
  QuestionnaireResponseSchema,
  UserInfoResponseSchema,
  SubmitFeedbackResponseSchema,
  UpdateNotificationsResponseSchema,
  UpdateTimezoneResponseSchema,
  ResetResponseSchema,
} from './api.schemas';

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  'https://physician-courts-ut-spotlight.trycloudflare.com';
const DEBUG_MODE = process.env.REACT_APP_DEBUG_MODE === 'true';
const API_TIMEOUT = parseInt(process.env.REACT_APP_API_TIMEOUT || '10000', 10);
const RETRY_COUNT = parseInt(process.env.REACT_APP_API_RETRY_COUNT || '2', 10);

// ========== ТИПЫ ПО ДОКУМЕНТАЦИИ ==========

export interface RecommendProduct {
  product_id: string;
  product_name: string;
  product_url: string;
  product_photo: string;
  description: string;
  recommend_text?: string | null;
}

export interface TelegramAuthResponse {
  success: boolean;
  token: string;
  user: {
    id_telegram: number;
    questionary_done: boolean;
    first_name: string | null;
    last_name?: string | null;
    username?: string | null;
    age?: number | null;
    feedback?: string | null;
    skin_type?: string | null;
    is_sensitive?: boolean | null;
    is_sick?: boolean | null;
    use_retinol_before?: boolean | null;
    count_weeks_used_retinol_before?: number | null;
    frequency_weeks_used_retinol_before?: number | null;
    last_use_retinol?: string | null;
    plan_start_use_retinol?: string | null;
    stage?: number | null;
    need_notifications?: boolean | null;
    language_code?: string | null;
    is_premium?: boolean | null;
    photo_url?: string | null;
  };
  dates?: string[];
  recommend_products?: RecommendProduct[];
}

export interface QuestionnaireData {
  age: number;
  skin_type: string;
  is_sensitive: boolean;
  is_sick: boolean;
  use_retinol_before: boolean;
  count_weeks_used_retinol_before?: number | null;
  last_use_retinol?: string | null;
  plan_start_use_retinol?: string | null;
  need_notifications: boolean;
  timezone: string;
}

export interface QuestionnaireResponse {
  success: boolean;
  dates?: string[] | null;
  recommend_products?: RecommendProduct[] | null;
}

export interface UserInfoResponse {
  id_telegram: number;
  questionary_done: boolean;
  real_name?: string | null; // ✅ БАГ 3 FIX: бекенд отдает real_name вместо first_name
  first_name?: string | null; // Legacy поле (deprecated)
  last_name?: string | null;
  username?: string | null;
  photo_url?: string | null;
  age?: number | null;
  skin_type?: string | null;
  is_sensitive?: boolean | null;
  is_sick?: boolean | null;
  use_retinol_before?: boolean | null;
  plan_start_use_retinol?: string | null;
  need_notifications?: boolean | null;
  dates?: string[] | null;
  recommend_products?: RecommendProduct[] | null;
}

export interface SubmitFeedbackRequest {
  feedback_text: string; // ✅ ИСПРАВЛЕНО: бекенд ожидает feedback_text, а не messages
}

export interface SubmitFeedbackResponse {
  success: boolean;
}

export interface UpdateNotificationsRequest {
  is_need_notifications: boolean;
}

export interface UpdateNotificationsResponse {
  success: boolean;
  error?: string | null;
}

export interface UpdateTimezoneRequest {
  timezone: string;
}

export interface UpdateTimezoneResponse {
  success: boolean;
}

// ========== ApiError ==========

export class ApiError extends Error {
  status: number;
  code?: string;
  payload?: any;

  constructor(opts: { status: number; message: string; code?: string; payload?: any }) {
    super(opts.message);
    this.name = 'ApiError';
    this.status = opts.status;
    this.code = opts.code;
    this.payload = opts.payload;
  }
}

// ========== ЛОГГЕР-ОБЁРТКА ДЛЯ API ==========

const logger = {
  request(method: string, url: string, data?: any) {
    if (!DEBUG_MODE) return;
    appLogger.debug(`🔵 API Request: ${method} ${url}`, data);
  },
  response(method: string, url: string, status: number, data: any) {
    if (!DEBUG_MODE) return;
    appLogger.debug(`🟢 API Response: ${method} ${url}`, { status, data });
  },
  error(method: string, url: string, error: string) {
    appLogger.error(`🔴 API Error: ${method} ${url}`, error);
  },
};

// ========== КЛИЕНТ ==========

class RetinolAPIClient {
  private baseUrl: string;
  private token: string | null;
  private timeout: number;
  private retryCount: number;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.token = localStorage.getItem('auth_token');
    this.timeout = API_TIMEOUT;
    this.retryCount = RETRY_COUNT;

    if (DEBUG_MODE) {
      appLogger.debug('🚀 API Client initialized:', {
        baseUrl: this.baseUrl,
        hasToken: !!this.token,
        debugMode: DEBUG_MODE,
      });
    }
  }

  private getHeaders(includeAuth = true): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };

    // 🔧 ИСПРАВЛЕНО: корректная подстановка Bearer-токена с fallback и trim
    if (includeAuth) {
      const raw = this.token || localStorage.getItem('auth_token') || '';
      const token = raw.trim();

      if (token) {
        if (!this.token) this.token = token; // синхронизация in-memory
        headers.Authorization = `Bearer ${token}`;

        if (DEBUG_MODE) {
          const masked = token.length > 8 ? `${token.slice(0, 4)}...${token.slice(-4)}` : '***';
          appLogger.debug(`🔐 Authorization установлен: Bearer ${masked}`);
        }
      } else if (DEBUG_MODE) {
        appLogger.warn(
          '⚠️ includeAuth=true, но валидный токен отсутствует — заголовок Authorization не будет отправлен'
        );
      }
    }

    return headers;
  }

  private sanitizeEndpoint(endpoint: string): string {
    if (!endpoint) return endpoint;
    if (/[?&]id_telegram=/i.test(endpoint) && DEBUG_MODE) {
      appLogger.warn('⚠️ endpoint содержит id_telegram в query:', endpoint);
    }
    return endpoint;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit & { includeAuth?: boolean } = {},
    retryAttempt = 0
  ): Promise<T> {
    const safeEndpoint = this.sanitizeEndpoint(endpoint);
    const url = `${this.baseUrl}${safeEndpoint}`;
    const method = options.method || 'GET';

    // ✅ Логируем URL только в development для диагностики
    if (process.env.NODE_ENV === 'development') {
      console.log(`🌐 API Request: ${method} ${url}`);
    }

    const { includeAuth: optIncludeAuth, ...fetchOptions } = options as any;
    const includeAuth = optIncludeAuth !== false;

    let requestBodyForLog: any = null;
    if (fetchOptions.body) {
      try {
        requestBodyForLog =
          typeof fetchOptions.body === 'string'
            ? JSON.parse(fetchOptions.body)
            : fetchOptions.body;
      } catch {
        requestBodyForLog = '[unparsed body]';
      }
    }
    logger.request(method, safeEndpoint, requestBodyForLog);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(url, {
        ...fetchOptions,
        headers: {
          ...this.getHeaders(includeAuth),
          ...(fetchOptions.headers as Record<string, string> | undefined),
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const text = await response.text();
      const contentType = (response.headers.get('content-type') || '').toLowerCase();

      let data: any = {};
      if (text) {
        if (contentType.includes('application/json')) {
          try {
            data = JSON.parse(text);
          } catch (parseError: unknown) {
            const parseErrorMsg =
              parseError instanceof Error ? parseError.message : 'Ошибка парсинга';
            logger.error(method, safeEndpoint, `Parse error: ${parseErrorMsg}`);
            data = { raw: text };
          }
        } else {
          data = { raw: text };
        }
      }

      if (!response.ok) {
        if (response.status === 401 && DEBUG_MODE) {
          const hasAuthHeader = !!this.getHeaders(true).Authorization;
          appLogger.warn('🛑 401 от сервера. Был ли отправлен Authorization?', {
            hasAuthHeader,
          });
        }

        const serverMessage: string =
          data?.message ||
          data?.detail?.message ||
          data?.error_description ||
          (typeof data?.raw === 'string' && data.raw.trim().slice(0, 300)) ||
          `HTTP ${response.status}`;
        const serverCode: string | undefined =
          data?.error || data?.detail?.error || undefined;

        if (DEBUG_MODE) {
          appLogger.warn('❗️HTTP error payload', {
            status: response.status,
            contentType,
            raw: typeof text === 'string' ? text.slice(0, 500) : '',
          });
        }

        // ✅ FIX: Централизованная обработка 401
        if (response.status === 401 && this.token) {
          this.clearToken();

          // Вызываем logout в authStore для полной очистки и редиректа
          // Используем динамический импорт чтобы избежать циклической зависимости
          setTimeout(async () => {
            try {
              const { useAuthStore } = await import('../store/authStore');
              const { logout } = useAuthStore.getState();
              logout();
              appLogger.warn('🚪 Автоматический logout из-за 401 Unauthorized');
            } catch (logoutError) {
              console.error('❌ Ошибка вызова logout при 401:', logoutError);
            }
          }, 0);
        }

        logger.error(
          method,
          safeEndpoint,
          `HTTP ${response.status}${serverCode ? ` ${serverCode}` : ''} ${serverMessage}`
        );

        throw new ApiError({
          status: response.status,
          message: serverMessage,
          code: serverCode,
          payload: data,
        });
      }

      logger.response(method, safeEndpoint, response.status, data);
      return (contentType.includes('application/json') ? data : (data as unknown)) as T;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Неизвестная ошибка';

      const isNetworkError =
        (error instanceof Error && error.name === 'AbortError') ||
        errorMessage.toLowerCase().includes('fetch') ||
        errorMessage.toLowerCase().includes('network');

      if (isNetworkError && retryAttempt < this.retryCount) {
        if (DEBUG_MODE) {
          appLogger.warn(
            `⚠️ Retry attempt ${retryAttempt + 1}/${this.retryCount} for ${safeEndpoint}`
          );
        }
        await this.delay(1000 * (retryAttempt + 1));
        return this.request<T>(endpoint, options, retryAttempt + 1);
      }

      logger.error(method, safeEndpoint, errorMessage);

      if (error instanceof ApiError) throw error;
      throw new ApiError({ status: 0, message: errorMessage });
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // ========== 🔧 ИСПРАВЛЕНО: АВТОРИЗАЦИЯ ==========
  /**
   * ✅ Авторизация через Telegram
   * ИСПРАВЛЕНО: Убран лишний заголовок X-Telegram-Init-Data
   * Согласно документации API, initData передаётся только в теле запроса
   */
  async authTelegram(initData: string): Promise<TelegramAuthResponse> {
    if (DEBUG_MODE) {
      appLogger.debug('🌐 Отправка запроса на бекенд /auth/telegram ...');
      appLogger.debug('📊 initData длина:', initData?.length || 0);
      appLogger.debug('📊 Первые 100 символов:', initData.substring(0, 100));
    }

    // ✅ ПРАВИЛЬНО: отправляем только JSON body согласно документации
    const rawResponse = await this.request<unknown>('/auth/telegram', {
      method: 'POST',
      body: JSON.stringify({ initData }), // ✅ { "initData": "..." }
      includeAuth: false, // ❗ Не подставляем старый Bearer токен
    });

    // ✅ FIX: Runtime validation с zod для защиты от невалидных данных
    let response: TelegramAuthResponse;
    try {
      response = TelegramAuthResponseSchema.parse(rawResponse);
    } catch (validationError) {
      const errorMsg = validationError instanceof Error ? validationError.message : 'Validation failed';
      throw new ApiError({
        status: 200,
        message: `Невалидный ответ от API /auth/telegram: ${errorMsg}`,
        payload: rawResponse,
      });
    }

    // ✅ FIX: Безопасное сохранение токена с проверкой типа
    if (response.success && response.token) {
      this.token = response.token;
      localStorage.setItem('auth_token', response.token);

      if (DEBUG_MODE) {
        // ✅ FIX: Проверяем что token строка перед вызовом .substring()
        const masked = typeof response.token === 'string' && response.token.length > 8
          ? `${response.token.substring(0, 4)}...${response.token.slice(-4)}`
          : '***';
        appLogger.debug('✅ Токен сохранён:', masked);
      }
    }

    return response;
  }

  // ===== АНКЕТА =====
  async submitQuestionnaire(data: QuestionnaireData): Promise<QuestionnaireResponse> {
    const endpoint = '/process_questionary';
    if (DEBUG_MODE) {
      appLogger.debug('📤 API submitQuestionnaire:', {
        url: `${this.baseUrl}${endpoint}`,
        body: data,
      });
    }
    const rawResponse = await this.request<unknown>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    // ✅ FIX: Runtime validation
    try {
      return QuestionnaireResponseSchema.parse(rawResponse);
    } catch (validationError) {
      const errorMsg = validationError instanceof Error ? validationError.message : 'Validation failed';
      throw new ApiError({
        status: 200,
        message: `Невалидный ответ от API ${endpoint}: ${errorMsg}`,
        payload: rawResponse,
      });
    }
  }

  // ===== ПОЛУЧЕНИЕ ИНФОРМАЦИИ О ПОЛЬЗОВАТЕЛЕ =====
  async getUserInfo(): Promise<UserInfoResponse> {
    const endpoint = '/get_user_info';
    if (DEBUG_MODE) appLogger.debug('ℹ️ getUserInfo без id_telegram в query');
    const rawResponse = await this.request<unknown>(endpoint, { method: 'GET' });

    // ✅ FIX: Runtime validation
    try {
      return UserInfoResponseSchema.parse(rawResponse);
    } catch (validationError) {
      const errorMsg = validationError instanceof Error ? validationError.message : 'Validation failed';
      throw new ApiError({
        status: 200,
        message: `Невалидный ответ от API ${endpoint}: ${errorMsg}`,
        payload: rawResponse,
      });
    }
  }

  // ===== ОБРАТНАЯ СВЯЗЬ =====
  async submitFeedback(data: SubmitFeedbackRequest): Promise<SubmitFeedbackResponse> {
    const endpoint = '/feedback';
    const rawResponse = await this.request<unknown>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    // ✅ FIX: Runtime validation
    try {
      return SubmitFeedbackResponseSchema.parse(rawResponse);
    } catch (validationError) {
      const errorMsg = validationError instanceof Error ? validationError.message : 'Validation failed';
      throw new ApiError({
        status: 200,
        message: `Невалидный ответ от API ${endpoint}: ${errorMsg}`,
        payload: rawResponse,
      });
    }
  }

  // ===== ПРОЧЕЕ =====
  async markApplication(date: string): Promise<{ success: boolean }> {
    if (DEBUG_MODE) appLogger.debug('📝 markApplication (stub):', date);
    return { success: true };
  }

  // ===== СБРОС ДАННЫХ =====
  async reset(): Promise<{ success: boolean }> {
    const endpoint = '/reset';
    if (DEBUG_MODE) {
      appLogger.debug('🔄 Запрос на сброс данных пользователя');
      appLogger.debug('🌐 API_BASE:', this.baseUrl);
      appLogger.debug('🔗 Полный URL будет:', `${this.baseUrl}${endpoint}`);
    }
    const rawResponse = await this.request<unknown>(endpoint, {
      method: 'POST',
    });

    // ✅ FIX: Runtime validation
    try {
      return ResetResponseSchema.parse(rawResponse);
    } catch (validationError) {
      const errorMsg = validationError instanceof Error ? validationError.message : 'Validation failed';
      throw new ApiError({
        status: 200,
        message: `Невалидный ответ от API ${endpoint}: ${errorMsg}`,
        payload: rawResponse,
      });
    }
  }

  // ===== ОБНОВЛЕНИЕ УВЕДОМЛЕНИЙ =====
  async updateNotifications(data: UpdateNotificationsRequest): Promise<UpdateNotificationsResponse> {
    const endpoint = '/need_notification';
    if (DEBUG_MODE) {
      appLogger.debug('🔔 Обновление настроек уведомлений:', data);
    }
    const rawResponse = await this.request<unknown>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    // ✅ FIX: Runtime validation
    try {
      return UpdateNotificationsResponseSchema.parse(rawResponse);
    } catch (validationError) {
      const errorMsg = validationError instanceof Error ? validationError.message : 'Validation failed';
      throw new ApiError({
        status: 200,
        message: `Невалидный ответ от API ${endpoint}: ${errorMsg}`,
        payload: rawResponse,
      });
    }
  }

  // ===== ОБНОВЛЕНИЕ TIMEZONE =====
  async updateTimezone(data: UpdateTimezoneRequest): Promise<UpdateTimezoneResponse> {
    const endpoint = '/change_timezone';
    if (DEBUG_MODE) {
      appLogger.debug('🌍 Обновление timezone:', data);
    }
    const rawResponse = await this.request<unknown>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    // ✅ FIX: Runtime validation
    try {
      return UpdateTimezoneResponseSchema.parse(rawResponse);
    } catch (validationError) {
      const errorMsg = validationError instanceof Error ? validationError.message : 'Validation failed';
      throw new ApiError({
        status: 200,
        message: `Невалидный ответ от API ${endpoint}: ${errorMsg}`,
        payload: rawResponse,
      });
    }
  }

  setToken(token: string) {
    this.token = (token || '').trim() || null;
    if (this.token) {
      localStorage.setItem('auth_token', this.token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('auth_token');
  }

  isAuthenticated(): boolean {
    return !!this.token;
  }

  async checkHealth(): Promise<{ status: 'ok' | 'error'; message?: string }> {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      if (response.ok || response.status === 404) {
        return { status: 'ok' };
      }
      return { status: 'error', message: `HTTP ${response.status}` };
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'network_error';
      return { status: 'error', message: errorMessage };
    }
  }
}

export const apiClient = new RetinolAPIClient(API_BASE);

export const checkApiHealth = async (): Promise<{ status: 'ok' | 'error'; message?: string }> => {
  return apiClient.checkHealth();
};

// ===== УТИЛИТЫ ДАТ =====

/** "2025-10-13T14:12:46.838Z" -> "2025-10-13" */
export function dateTimeToDate(dateTime: string): string {
  return dateTime.split('T')[0];
}

/** "2025-10-13" -> "2025-10-13T00:00:00.000Z" */
export function dateToISO(date: string): string {
  return `${date}T00:00:00.000Z`;
}

/** Приводит массив дат к формату "YYYY-MM-DD" */
export function normalizeDates(dates: string[]): string[] {
  return dates.map((date) => (date.includes('T') ? dateTimeToDate(date) : date));
}
